// CreativePage.jsx
import React, { useEffect } from 'react';
import { Row, Col, Button, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import xsimage from '../../assets/images/branding-xs.png'

const CreativePage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
        // Scroll animation observer
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const creativeStats = [
        { number: '88%', label: 'of consumers say authenticity matters when choosing brands to support' },
        { number: '3.5x', label: 'more engagement for video content compared to static posts' },
        { number: '70%', label: 'of marketers say creative quality directly impacts campaign performance' },
        { number: '40%', label: 'higher conversion rates with consistent brand storytelling' }
    ];

    const creativeApproaches = [
        {
            icon: 'fas fa-bullseye',
            title: 'Creative Campaign Development',
            desc: 'Strategic campaign visioning, messaging frameworks, and cross-platform execution with visual and narrative strategies designed to break through digital noise.'
        },
        {
            icon: 'fas fa-pen-nib',
            title: 'Copywriting & Brand Messaging',
            desc: 'Persuasive, audience-centric messaging development including performance-focused digital copy, SEO-aware content, landing page optimization, and conversion copywriting.'
        },
        {
            icon: 'fas fa-video',
            title: 'Video Production & Motion Content',
            desc: 'High-quality video content production including social videos (Reels, TikToks, Shorts), promotional and product videos, animation, and motion design for maximum engagement.'
        },
        {
            icon: 'fas fa-camera-retro',
            title: 'Creative Production & Execution',
            desc: 'Professional creative production with studio-grade quality, branded content development, experiential visuals, localized storytelling, and talent sourcing.'
        }
    ];

    const benefits = [
        ['Distinctive Brand Identity', 'Build a unique visual identity and compelling narrative that stands out in crowded digital landscapes'],
        ['Enhanced Audience Engagement', 'Create meaningful connections through emotional storytelling, compelling visuals, and resonant messaging'],
        ['Improved Conversion Performance', 'Drive higher engagement rates and conversion metrics with strategically optimized creative assets'],
        ['Cross-Platform Creative Consistency', 'Maintain cohesive brand expression and messaging across all digital channels and touchpoints'],
        ['Performance-Optimized Creative Output', 'Align artistic excellence with data-driven insights for measurable business impact and ROI']
    ];

    const targetBusinesses = [
        'E-commerce businesses looking to boost product visibility, showcase products effectively, and drive conversions through compelling visuals',
        'B2B brands needing compelling narratives that shorten sales cycles, build authority, and communicate complex value propositions',
        'Startups building identity and brand awareness, establishing market presence, and creating foundational creative assets',
        'Enterprises seeking large-scale, integrated creative campaigns with consistent cross-channel execution and brand alignment',
        'Performance marketing teams that want creative aligned with KPIs, optimized for specific metrics, and built for conversion'
    ];

    const creativeQuestions = [
        'How can your brand stand out in increasingly crowded digital environments?',
        'What creative approaches drive the highest engagement and conversion for your audience?',
        'How do you maintain consistent brand expression across multiple platforms and channels?',
        'What visual and narrative strategies effectively communicate your brand story and values?',
        'How can creative innovation support your business objectives and drive measurable growth?'
    ];

    const creativeTechniques = [
        'Strategic Creative Concept Development & Campaign Planning',
        'Cross-Platform Visual Storytelling & Content Creation',
        'Performance-Optimized Copywriting & Messaging Strategy',
        'Video Production & Motion Graphics for Digital Engagement',
        'Brand Identity Systems & Visual Design Excellence',
        'Creative Testing, Optimization & Performance Analytics'
    ];

    const creativeComponents = [
        { component: 'Strategic Campaign Concepts', impact: 'Better audience engagement and recall, improved brand visibility' },
        { component: 'Compelling Copywriting', impact: 'Higher conversions and click-through rates, clearer brand communication' },
        { component: 'Captivating Video Content', impact: 'Greater reach and social engagement, improved audience retention' },
        { component: 'Professional Production', impact: 'Stronger brand authority and trust, enhanced brand perception' }
    ];

    const whyCreativeItems = [
        '🎨 Build a distinctive visual identity that stands out in crowded feeds',
        '✍️ Tell your brand story with voice and purpose',
        '📣 Connect emotionally with customers through compelling messaging',
        '🚀 Drive engagement and conversions with creative-first campaigns',
        '📊 Align creative output with performance goals and analytics'
    ];

    return (
        <div className="branding sv-page">
            {/* Decorative Image - Top of Page */}
            <div className="sv-decorative-image-container">

            </div>
            <div className='xs-sv-page'>
                <img src={xsimage} />
            </div>
            {/* Page Title Section */}
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">Creative Services</span>
                    </h1>
                    <p>Elevate Your Brand with Strategic Creativity and Digital Storytelling</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    {/* Hero Content Section */}
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    In today's attention-driven digital landscape, creativity isn't just about visuals — it's about meaningful engagement, compelling storytelling, and measurable impact.
                                </p>
                                <p className="sv-description">
                                    Reachtix's Creative Services blend artistic innovation with performance-focused strategy to make your brand stand out, resonate with audiences and drive real business results across digital channels. We believe creative work should do more than look good — it should capture attention, communicate purpose, and inspire action.
                                </p>
                                <p className="sv-description">
                                    Our approach combines design thinking, narrative craft and digital optimisation to create experiences that work as hard as they look.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-rocket mr-2"></i>
                                        Start Creative Project
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-eye mr-2"></i>
                                        Explore Creative Services
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Stats Section */}
                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            The <span className="sv-text-gradient">Creative Impact Advantage</span>
                        </h2>
                        <Row className="sv-stats-grid">
                            {creativeStats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    {/* Why Creative Services Are Essential Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">Why Creative Services Are Essential</h2>
                                <p className="sv-section-description">
                                    Creativity is the bridge between your brand and your audience. It helps you:
                                </p>
                                <div className="sv-questions-grid">
                                    {whyCreativeItems.map((item, index) => (
                                        <div key={index} className="sv-question-item">
                                            <span className="sv-question-icon">{item.substring(0, 2)}</span>
                                            {item.substring(3)}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-3">
                                    Creativity isn't just art — it's strategic communication that elevates your entire marketing ecosystem.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* What Is Creative Services Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What Reachtix Offers</h2>
                                <p className="sv-section-description">
                                    Our Creative Services are designed to integrate with your wider digital strategy and amplify impact across platforms.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Creative Questions We Address:</h4>
                                    {creativeQuestions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Our Creative Approach Section */}
                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Our Creative Capabilities at Reachtix</h2>
                                <p className="sv-section-description">
                                    Our Creative Services are designed to integrate with your wider digital strategy and amplify impact across platforms, combining artistic vision with commercial thinking to deliver experiences that are both memorable and measurable.
                                </p>

                                <div className="sv-approaches-grid">
                                    {creativeApproaches.map((item, index) => (
                                        <div key={index} className="sv-approach-card">
                                            <div className="sv-approach-icon">
                                                <i className={item.icon}></i>
                                            </div>
                                            <div className="sv-approach-content">
                                                <h4>{item.title}</h4>
                                                <p>{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Campaign Details Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={12}>
                                <h2 className="sv-section-title">Creative Campaign Development</h2>
                                <p className="sv-section-description">
                                    We create immersive creative campaigns that help your brand break through digital noise and connect with your audience at the right moment. From concept to execution, everything we produce is strategically aligned with your business goals:
                                </p>
                                <div className="sv-questions-grid">
                                    <div className="sv-question-item"><i className="fas fa-check-circle"></i> Campaign vision and messaging frameworks</div>
                                    <div className="sv-question-item"><i className="fas fa-check-circle"></i> Cross-platform execution (social, paid, organic)</div>
                                    <div className="sv-question-item"><i className="fas fa-check-circle"></i> Visual and narrative strategy</div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={12}>
                                <h2 className="sv-section-title">Copywriting & Brand Messaging</h2>
                                <p className="sv-section-description">
                                    Words matter. Our expert copywriters craft persuasive, audience-centric messaging — from scroll-stopping headlines to conversion-focused calls-to-action — designed to capture attention and drive results.
                                </p>
                                <div className="sv-questions-grid">
                                    <div className="sv-question-item"><i className="fas fa-check-circle"></i> Performance-focused digital copy</div>
                                    <div className="sv-question-item"><i className="fas fa-check-circle"></i> SEO-aware content messaging</div>
                                    <div className="sv-question-item"><i className="fas fa-check-circle"></i> Landing page and ad copywriting</div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={12}>
                                <h2 className="sv-section-title">Video Production & Motion Content</h2>
                                <p className="sv-section-description">
                                    Video continues to be one of the most engaging formats online. We produce high-quality video content tailored for your brand and audience, whether it's short-form social clips or longer storytelling pieces.
                                </p>
                                <div className="sv-questions-grid">
                                    <div className="sv-question-item"><i className="fas fa-check-circle"></i> Social videos (Reels, TikToks, Shorts)</div>
                                    <div className="sv-question-item"><i className="fas fa-check-circle"></i> Promotional and product videos</div>
                                    <div className="sv-question-item"><i className="fas fa-check-circle"></i> Animated and motion design</div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={12}>
                                <h2 className="sv-section-title">Creative Production & Execution</h2>
                                <p className="sv-section-description">
                                    We bring powerful ideas to life with professional creative production, using best-in-class talent and tools to craft polished content that resonates.
                                </p>
                                <div className="sv-questions-grid">
                                    <div className="sv-question-item"><i className="fas fa-check-circle"></i> Studio-grade production and editing</div>
                                    <div className="sv-question-item"><i className="fas fa-check-circle"></i> Branded content and experiential visuals</div>
                                    <div className="sv-question-item"><i className="fas fa-check-circle"></i> Localised storytelling and talent sourcing</div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Creative Components Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Creative Services That Support Performance</h2>
                                <p className="sv-section-description mb-4">
                                    At Reachtix, creativity isn't isolated — it's rooted in performance metrics and audience insights. This performance-first creative approach ensures your campaigns are both memorable and measurable.
                                </p>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Creative Component</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {creativeComponents.map((component, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{component.component}</strong>
                                                    </td>
                                                    <td>{component.impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Benefits of Creative Services with Reachtix</h2>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Strategic Advantage</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([advantage, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{advantage}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                                <p className="mt-4 sv-section-description">
                                    Strategic creativity transforms your brand communication from functional to exceptional, enabling distinctive market positioning, deeper audience connections, improved engagement metrics, and measurable return on creative investment.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Who Should Invest Section */}
                    <section className="sv-target-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Who Benefits from Reachtix's Creative Services?</h2>
                                <p className="sv-section-description">
                                    Our creative expertise supports a wide range of brands and goals:
                                </p>
                                <div className="sv-target-list">
                                    {targetBusinesses.map((business, index) => (
                                        <div key={index} className="sv-target-item">
                                            <i className="fas fa-chevron-right"></i>
                                            {business}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-4">
                                    Creativity isn't just for brands with big budgets — it's a strategic advantage for any business that wants to be seen, heard, and remembered.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Why Partner With Reachtix Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">Why Choose Reachtix for Creative Services?</h2>
                                <p className="sv-section-description">
                                    At Reachtix, we're not just a creative agency — we're creative strategists with performance expertise. We blend artistic vision with data-driven decision-making, equipping your brand with creative that looks amazing and performs measurably.
                                </p>
                                <p className="sv-section-description">
                                    Our creative process includes: insight-led concepting, audience-driven execution, continuous optimisation, and cross-channel compatibility. We act as an extension of your team — bringing creativity, curiosity, and commercial thinking to every project.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Our Creative Advantages:</h4>
                                    <div className="sv-question-item">
                                        <i className="fas fa-lightbulb"></i>
                                        Insight-Led Concepting & Strategic Creative Development
                                    </div>
                                    <div className="sv-question-item">
                                        <i className="fas fa-users"></i>
                                        Audience-Driven Execution & Performance-Optimized Creative
                                    </div>
                                    <div className="sv-question-item">
                                        <i className="fas fa-sync-alt"></i>
                                        Continuous Optimization Based on Engagement Analytics
                                    </div>
                                    <div className="sv-question-item">
                                        <i className="fas fa-network-wired"></i>
                                        Cross-Channel Compatibility & Integrated Brand Expression
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Final CTA Section */}
                    <section className="sv-main-cta" data-animated>
                        <Row>
                            <Col>
                                <h2>Ready to Unleash Creative Impact?</h2>
                                <p className="sv-cta-description">
                                    Creativity is more than visuals — it's brand performance in motion. If you want creative work that not only captures attention but drives results, Reachtix's Creative Services are your competitive edge.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='primary'
                                    >
                                        <i className="fas fa-paint-brush mr-2"></i>
                                        Start Creative Project
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='light'
                                    >
                                        <i className="fas fa-eye mr-2"></i>
                                        Get Free Creative Audit
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default CreativePage;